/**
 * Une étape du parcours d'envoi — chunk 9.AZ.1.
 *
 * La numérotation est un repère de navigation, pas une obligation séquentielle.
 * Mandat et cahier peuvent avancer en parallèle ; le serveur décide de l'envoi.
 *
 * ## Trois états, jamais un seul avec du texte de remplacement
 *
 * `empty` — il manque quelque chose · `ready` — c'est prêt · `stale` — il y a un
 * geste à faire. La couleur ne porte jamais l'information seule : chaque état a
 * son libellé.
 */

import { useTranslation } from "react-i18next";

export type EtatEtape = "empty" | "ready" | "stale";

const TON: Record<EtatEtape, { pastille: string; texte: string }> = {
  empty: { pastille: "bg-slate-100 text-slate-600", texte: "text-slate-500" },
  ready: { pastille: "bg-emerald-50 text-emerald-800", texte: "text-emerald-700" },
  stale: { pastille: "bg-amber-50 text-amber-900", texte: "text-amber-800" },
};

export function Etape(props: {
  numero: number;
  titre: string;
  description?: string;
  etat: EtatEtape;
  statusLabel?: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const ton = TON[props.etat];

  return (
    <section
      aria-label={props.titre}
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold tabular-nums text-slate-600"
          >
            {props.numero}
          </span>
          <div className="min-w-0">
            <h2 className="text-pretty text-base font-semibold text-slate-900">{props.titre}</h2>
            {props.description && (
              <p className="mt-0.5 text-sm text-slate-600">{props.description}</p>
            )}
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${ton.pastille}`}>
          {props.statusLabel ?? t(`prepare_send.state.${props.etat}`)}
        </span>
      </header>
      {props.children}
    </section>
  );
}
